import { Logger } from '../../../utils/logger';

export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_RAW_IMAGE_BYTES = 16 * 1024 * 1024;
export const MIN_IMAGE_DIMENSION = 32;
export const MAX_IMAGE_DIMENSION = 512;
export const OUTPUT_IMAGE_MIME_TYPE = 'image/webp' as const;

export type ImageProcessingErrorCode =
  | 'UNSUPPORTED_MIME_TYPE'
  | 'FILE_TOO_LARGE'
  | 'INVALID_IMAGE'
  | 'IMAGE_TOO_SMALL'
  | 'IMAGE_PROCESSING_FAILED';

export class ImageProcessingError extends Error {
  constructor(
    public readonly code: ImageProcessingErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ImageProcessingError';
  }
}

export type ProcessedImageResult = {
  blob: Blob;
  mimeType: typeof OUTPUT_IMAGE_MIME_TYPE;
  contentLength: number;
  width: number;
  height: number;
};

const PNG_FILE_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

function bytesMatchPrefix(bytes: Uint8Array, prefix: readonly number[]): boolean {
  if (bytes.length < prefix.length) return false;
  return prefix.every((b, i) => bytes[i] === b);
}

function isGifSignature(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  );
}

function isPngSignature(bytes: Uint8Array): boolean {
  return bytesMatchPrefix(bytes, PNG_FILE_SIGNATURE);
}

function isJpegSignature(bytes: Uint8Array): boolean {
  return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

function isWebpSignature(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  if (bytes[0] !== 0x52 || bytes[1] !== 0x49 || bytes[2] !== 0x46 || bytes[3] !== 0x46) return false;
  return bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
}

function isBmpSignature(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d;
}

function isIcoSignature(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x01 && bytes[3] === 0x00;
}

function looksLikeSvgMarkup(head: Uint8Array): boolean {
  const sample = head.subarray(0, Math.min(head.length, 256));
  const text = new TextDecoder('utf-8', { fatal: false }).decode(sample);
  const trimmed = text.trimStart().toLowerCase();
  return trimmed.startsWith('<svg') || trimmed.startsWith('<?xml');
}

function isAllowedRasterSignature(head: Uint8Array): boolean {
  return isPngSignature(head) || isJpegSignature(head) || isWebpSignature(head);
}

function readBlobViaFileReader(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(result);
        return;
      }
      reject(new Error('Unexpected FileReader result'));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('FileReader failed'));
    };
    reader.readAsArrayBuffer(blob);
  });
}

async function readFileHead(file: File, maxBytes: number): Promise<Uint8Array> {
  const byteCount = Math.min(maxBytes, Math.max(0, file.size));
  if (byteCount === 0) return new Uint8Array(0);
  const slice = file.slice(0, byteCount);
  if (typeof slice.arrayBuffer === 'function') {
    try {
      const buffer = await slice.arrayBuffer();
      return new Uint8Array(buffer);
    } catch {
      /* fall through */
    }
  }
  if (typeof file.arrayBuffer === 'function') {
    try {
      const full = await file.arrayBuffer();
      return new Uint8Array(full).subarray(0, byteCount);
    } catch {
      /* fall through */
    }
  }
  if (typeof FileReader !== 'undefined') {
    try {
      const buffer = await readBlobViaFileReader(slice);
      const out = new Uint8Array(buffer);
      if (out.byteLength > 0 || byteCount === 0) return out;
    } catch {
      /* fall through */
    }
  }
  const buffer = await new Response(slice).arrayBuffer();
  return new Uint8Array(buffer);
}

function assertDisallowedFileSignatures(head: Uint8Array): void {
  if (isGifSignature(head)) {
    throw new ImageProcessingError('UNSUPPORTED_MIME_TYPE', 'GIF is not supported.');
  }
  if (isBmpSignature(head)) {
    throw new ImageProcessingError('UNSUPPORTED_MIME_TYPE', 'BMP is not supported.');
  }
  if (isIcoSignature(head)) {
    throw new ImageProcessingError('UNSUPPORTED_MIME_TYPE', 'ICO is not supported.');
  }
  if (looksLikeSvgMarkup(head) && !isAllowedRasterSignature(head)) {
    throw new ImageProcessingError('UNSUPPORTED_MIME_TYPE', 'SVG is not supported.');
  }
}

function assertEmptyMimeRequiresKnownMagic(file: File, head: Uint8Array): void {
  const declared = (file.type || '').trim();
  if (declared !== '') return;
  if (!isAllowedRasterSignature(head)) {
    throw new ImageProcessingError(
      'UNSUPPORTED_MIME_TYPE',
      'Only JPEG, PNG, and WebP images are supported.',
    );
  }
}

/** Validates raw file MIME type and size limits before decoding. */
function assertRawInput(file: File): void {
  const declared = (file.type || '').trim();
  if (declared !== '' && !ALLOWED_IMAGE_MIME_TYPES.includes(declared as (typeof ALLOWED_IMAGE_MIME_TYPES)[number])) {
    throw new ImageProcessingError(
      'UNSUPPORTED_MIME_TYPE',
      'Only JPEG, PNG, and WebP images are supported.',
    );
  }
  if (file.size > MAX_RAW_IMAGE_BYTES) {
    Logger.debug('MediaImageProcess', 'FILE_TOO_LARGE', {
      name: file.name,
      type: file.type,
      sizeBytes: file.size,
      limitBytes: MAX_RAW_IMAGE_BYTES,
    });
    throw new ImageProcessingError(
      'FILE_TOO_LARGE',
      `Image must be ${Math.round(MAX_RAW_IMAGE_BYTES / (1024 * 1024))}MB or smaller before processing.`,
    );
  }
}

/** Computes bounded output dimensions while preserving aspect ratio and avoiding upscaling. */
function computeOutputDimensions(width: number, height: number): { width: number; height: number } {
  if (width <= MAX_IMAGE_DIMENSION && height <= MAX_IMAGE_DIMENSION) {
    return { width, height };
  }
  const scale = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Decodes a browser File into an HTMLImageElement via object URL. */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new ImageProcessingError('INVALID_IMAGE', 'Failed to decode image file.'));
    };
    image.src = objectUrl;
  });
}

/** Draws the image to a canvas and encodes it as WebP. */
function encodeWebP(image: HTMLImageElement, width: number, height: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new ImageProcessingError('IMAGE_PROCESSING_FAILED', 'Failed to initialize canvas context.'));
      return;
    }
    context.drawImage(image, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new ImageProcessingError('IMAGE_PROCESSING_FAILED', 'Failed to encode image as WebP.'));
        return;
      }
      resolve(blob);
    }, OUTPUT_IMAGE_MIME_TYPE);
  });
}

/** Processes a user-selected image into a validated, resized WebP upload payload. */
export async function processImageForUpload(file: File): Promise<ProcessedImageResult> {
  assertRawInput(file);
  const head = await readFileHead(file, 64);
  assertDisallowedFileSignatures(head);
  assertEmptyMimeRequiresKnownMagic(file, head);
  const image = await loadImage(file);
  if (image.width < MIN_IMAGE_DIMENSION || image.height < MIN_IMAGE_DIMENSION) {
    throw new ImageProcessingError(
      'IMAGE_TOO_SMALL',
      `Image dimensions must be at least ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION}.`,
    );
  }

  const { width, height } = computeOutputDimensions(image.width, image.height);
  const blob = await encodeWebP(image, width, height);

  return {
    blob,
    mimeType: OUTPUT_IMAGE_MIME_TYPE,
    contentLength: blob.size,
    width,
    height,
  };
}
